import { useQuery } from "@tanstack/react-query";
import { ProductsRepository } from "./repositories/products-repository";
import { ClientProducts } from "./types/products";
import { Spin } from "@/ui/spin";
import { ReactNode } from "react";
import { Button, Empty, Result } from "antd";

export const useProducts = () => {
  return useQuery({
    queryKey: ["products"],
    queryFn: ProductsRepository.fetchProducts,
    staleTime: 600_000,
  });
};

export type ProductsProps = {
  products: ClientProducts[];
  isAvailable?: boolean;
};

type ProductsStateProps = {
  isLoading: boolean;
  error: Error | null;
  isEmpty?: boolean;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  emptyComponent?: ReactNode;
  children: ReactNode;
};

export const ProductsState = ({
  isLoading,
  error,
  isEmpty = false,
  loadingComponent,
  errorComponent,
  emptyComponent,
  children,
}: ProductsStateProps) => {
  if (isLoading) {
    return loadingComponent ?? <Spin fullscreen />;
  }

  if (error) {
    return (
      errorComponent ?? (
        <Result
          status="error"
          title="Ошибка. Попробуйте позднее."
          subTitle={error.message}
          extra={
            <Button onClick={() => window.location.reload()}>Refresh</Button>
          }
        />
      )
    );
  }

  if (isEmpty) {
    return (
      emptyComponent ?? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No products available"
        />
      )
    );
  }

  return <>{children}</>;
};

type WithProductsProps<P> = Omit<P, keyof ProductsProps> & {
  isAvailable: boolean;
}

export const withProducts = <P extends ProductsProps>(
  WrappedComponent: React.ComponentType<P>,
  options?: {
    loadingComponent?: ReactNode;
    errorComponent?: ReactNode;
    emptyComponent?: ReactNode;
  },
) => {
  return (props: WithProductsProps<P>) => {
    const { data: products, isLoading, error } = useProducts();

    const isEmpty = !products || products.length === 0;

    return (
      <ProductsState
        isLoading={isLoading}
        error={error}
        isEmpty={isEmpty}
        loadingComponent={options?.loadingComponent}
        errorComponent={options?.errorComponent}
        emptyComponent={options?.emptyComponent}
      >
        <WrappedComponent {...(props as P)} products={products || []} />
      </ProductsState>
    );
  };
};
